import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTab, MatTabChangeEvent, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { TranslatePipe } from '@next/core-lib/i18n';
import { tap } from 'rxjs';

import { AdviceMouseClickEvent } from 'src/app/shared/components/disease-overview-table.component/models/models';

import { BlightAIIdentifiers } from '../../../blight-v2/shared/constants';
import { BlightSignalStore } from '../../signal-store/blight-signal-store';
import { PageToolbarComponent } from '@next/core-lib/components';
import { NgTemplateOutlet } from '@angular/common';
import { OverviewComponent } from './components/overview/overview.component';
import { BlightInsightPageComponent } from './components/blight-insight-page/blight-insight-page.component';
import { CropYearSelectorComponent } from '../../../shared/components/crop-year-selector/crop-year-selector.component';

@Component({
  selector: 'app-blight-advice',
  templateUrl: './advice.component.html',
  styleUrl: './advice.component.scss',
  imports: [
    PageToolbarComponent,
    CropYearSelectorComponent,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    MatTabContent,
    OverviewComponent,
    BlightInsightPageComponent,
    TranslatePipe,
  ],
})
export class AdviceComponent {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(BlightSignalStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ai = inject(AppInsightsServiceInjector);

  private static readonly tabs = ['overview', 'insights'];

  protected readonly selectedTabIndex = signal(0);
  protected readonly lastSelectedCropField = signal<string | null>(null);

  protected readonly pageTitle = this.store.pageTitle;

  constructor() {
    this.route.queryParams
      .pipe(
        tap((params) => this.updateLastSelectedCropField(params)),
        tap((params: Params) => this.updateSelectedTab(params)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    effect(() => {
      const index = this.selectedTabIndex();
      if (AdviceComponent.tabs[index] === 'overview') {
        this.ai.trackEvent(BlightAIIdentifiers.clickTable, {});
      } else if (AdviceComponent.tabs[index] === 'insights') {
        this.ai.trackEvent(BlightAIIdentifiers.clickInsights, {});
      }
    });
  }

  protected async onTabChange(event: MatTabChangeEvent): Promise<void> {
    const selectedTab = AdviceComponent.tabs[event.index];
    this.selectedTabIndex.set(event.index);

    await this.router.navigate([], {
      queryParams: { tab: selectedTab, cropFieldId: this.lastSelectedCropField() },
      queryParamsHandling: 'merge',
    });
  }

  protected async onCropFieldClick(event: AdviceMouseClickEvent): Promise<void> {
    await this.router.navigate([], {
      queryParams: { tab: 'insights', cropFieldId: event.cropField.cropFieldId },
      queryParamsHandling: 'merge',
    });
  }

  private updateSelectedTab(params: Params): void {
    const tabName = params['tab'] || 'overview';
    const tabIndex = AdviceComponent.tabs.indexOf(tabName);
    this.selectedTabIndex.set(tabIndex === -1 ? 0 : tabIndex);
  }

  private async updateLastSelectedCropField(params: Params): Promise<void> {
    if (params['cropFieldId']) {
      this.lastSelectedCropField.set(params['cropFieldId']);
      return;
    }

    if (this.lastSelectedCropField()) {
      await this.router.navigate([], {
        queryParams: { cropFieldId: this.lastSelectedCropField() },
        queryParamsHandling: 'merge',
      });
    }
  }
}
